import { cancelOnSessionChange } from '../session-cancellation';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, map, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { OrganizationsService } from '@api/services/organizations.service';
import * as SubscriptionActions from '../../actions/organizations/subscription.actions';
import * as OrganizationsActions from '../../actions/organizations/core.actions';
import { MessageService } from 'primeng/api';
import { SubscriptionMutationStateService } from '../../../core/services/subscription-mutation-state.service';

@Injectable()
export class OrganizationsSubscriptionEffects {
  createOrUpdateSubscription$;
  cancelScheduledChange$;
  refreshOrganizationOnSuccess$;
  refreshOrganizationOnCancelSuccess$;
  refreshOrganizationOnPaymentSuccess$;
  showSuccessMessage$;
  showCancelScheduledChangeSuccessMessage$;
  showErrorMessage$;
  constructor(
    private actions$: Actions,
    private organizationsService: OrganizationsService,
    private messageService: MessageService,
    private mutationState: SubscriptionMutationStateService
  ) {

    this.createOrUpdateSubscription$ = createEffect(() =>
      this.actions$.pipe(
        ofType(SubscriptionActions.createOrUpdateSubscription),
        mergeMap(({ request, requestId }) => {
          const mutationId = this.mutationState.getOrCreateChange(request);
          return this.organizationsService.organizationsCreateOrUpdateSubscription$Response({
            organizationId: request.organizationId,
            body: {
              mutationId,
              planId: request.planId,
              paymentInterval: request.paymentInterval
            }
          }).pipe(
            map((response) => {
              // The response body may contain checkoutUrl for new paid subscriptions
              const responseBody = response.body;
              const organization = responseBody.organization;
              const checkoutUrl = responseBody.checkoutUrl;
              const subscription = responseBody.subscription;
              const scheduledChange = responseBody.scheduledChange;
              const mutationStatus = responseBody.mutationStatus;

              if (mutationStatus === 'expired' || mutationStatus === 'pending_update_expired') {
                this.mutationState.clearAuthoritativelyCompletedCheckout(mutationId);
                return SubscriptionActions.createOrUpdateSubscriptionFailure({
                  requestId,
                  mutationId,
                  request,
                  error: mutationStatus === 'pending_update_expired'
                    ? 'The upgrade payment expired. Your current plan was kept; please start the upgrade again.'
                    : 'The Checkout session expired. Please start the plan change again.'
                });
              }

              // For new paid subscriptions, checkoutUrl is provided for redirect to Stripe
              const requiresCheckoutRedirect = !!checkoutUrl;
              const requiresPayment = mutationStatus === 'pending_payment' ||
                mutationStatus === 'payment_action_required';
              if ((!requiresCheckoutRedirect && !requiresPayment) || mutationStatus === 'completed') {
                this.mutationState.clearAuthoritativelyCompletedCheckout(mutationId);
              }

              return SubscriptionActions.createOrUpdateSubscriptionSuccess({
                result: {
                  requestId,
                  mutationId,
                  request,
                  organization,
                  checkoutUrl,
                  subscription,
                  scheduledChange,
                  mutationStatus,
                  payment: responseBody.payment,
                  requiresCheckoutRedirect,
                  requiresPayment
                }
              });
            }),
            catchError((error) => {
              if (error?.error?.code === 'conflict-checkout-mutation-expired') {
                this.mutationState.clearAuthoritativelyCompletedCheckout(mutationId);
              }
              const errorMessage = error?.error?.message || error?.message || 'Failed to update subscription';
              return of(SubscriptionActions.createOrUpdateSubscriptionFailure({
                error: errorMessage,
                requestId,
                mutationId,
                request
              }));
            }),
            cancelOnSessionChange(this.actions$)
          );
        })
      )
    );

    // Cancel scheduled subscription change effect
    this.cancelScheduledChange$ = createEffect(() =>
      this.actions$.pipe(
        ofType(SubscriptionActions.cancelScheduledChange),
        mergeMap(({ request }) => {
          const mutationId = this.mutationState.getOrCreateScheduledCancellation(request);
          return this.organizationsService.organizationsCancelScheduledChange({
            organizationId: request.organizationId,
            body: { mutationId }
          }).pipe(
            map((response) => {
              this.mutationState.clearScheduledCancellation(request);
              return SubscriptionActions.cancelScheduledChangeSuccess({
                organization: response.organization
              });
            }),
            catchError((error) => {
              const errorMessage = error?.error?.message || error?.message || 'Failed to cancel scheduled change';
              return of(SubscriptionActions.cancelScheduledChangeFailure({ error: errorMessage }));
            }),
            cancelOnSessionChange(this.actions$)
          );
        })
      )
    );

    // Refresh organization data after successful subscription update (for free plan or existing subscription update)
    this.refreshOrganizationOnSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(SubscriptionActions.createOrUpdateSubscriptionSuccess),
        map(({ result }) => {
          // Only refresh immediately if no checkout redirect is needed
          if (!result.requiresCheckoutRedirect) {
            return OrganizationsActions.loadOrganizationById({ id: result.organization.id! });
          }
          // For new paid plans requiring checkout, the redirect will happen and we'll refresh on return
          return { type: '[Subscription] No-Op' };
        })
      )
    );

    // Refresh organization after cancel scheduled change success
    this.refreshOrganizationOnCancelSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(SubscriptionActions.cancelScheduledChangeSuccess),
        map(({ organization }) => OrganizationsActions.loadOrganizationById({ id: organization.id! }))
      )
    );

    // Refresh organization after payment confirmation success
    this.refreshOrganizationOnPaymentSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(SubscriptionActions.confirmPaymentSuccess),
        map(({ organizationId }) => OrganizationsActions.loadOrganizationById({ id: organizationId }))
      )
    );

    // Show success message for free plan or existing subscription update (immediate activation)
    this.showSuccessMessage$ = createEffect(() =>
      this.actions$.pipe(
        ofType(SubscriptionActions.createOrUpdateSubscriptionSuccess),
        tap(({ result }) => {
          if (!result.requiresCheckoutRedirect) {
            if (result.requiresPayment) {
              this.messageService.add({
                severity: result.mutationStatus === 'payment_action_required' ? 'warn' : 'info',
                summary: result.mutationStatus === 'payment_action_required'
                  ? 'Payment Action Required'
                  : 'Payment Pending',
                detail: 'Your current plan remains active until Stripe confirms the upgrade payment.',
                life: 10000
              });
              return;
            }
            // Check if this is a scheduled change (downgrade)
            if (result.scheduledChange) {
              const effectiveDate = new Date(result.scheduledChange.effectiveDate);
              const formattedDate = effectiveDate.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              });
              this.messageService.add({
                severity: 'info',
                summary: 'Downgrade Scheduled',
                detail: `Your plan will change to ${result.scheduledChange.scheduledPlanId} on ${formattedDate}.`,
                life: 8000
              });
            } else {
              this.messageService.add({
                severity: 'success',
                summary: 'Subscription Updated',
                detail: 'Your subscription has been updated successfully.',
                life: 5000
              });
            }
          }
        })
      ),
      { dispatch: false }
    );

    // Show success message for cancel scheduled change
    this.showCancelScheduledChangeSuccessMessage$ = createEffect(() =>
      this.actions$.pipe(
        ofType(SubscriptionActions.cancelScheduledChangeSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Scheduled Change Canceled',
            detail: 'Your scheduled plan change has been canceled. You will keep your current plan.',
            life: 5000
          });
        })
      ),
      { dispatch: false }
    );

    // Show error messages
    this.showErrorMessage$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          SubscriptionActions.createOrUpdateSubscriptionFailure, 
          SubscriptionActions.confirmPaymentFailure,
          SubscriptionActions.cancelScheduledChangeFailure
        ),
        tap(({ error }) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Subscription Error',
            detail: error,
            life: 8000
          });
        })
      ),
      { dispatch: false }
    );
  }

}
